import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Loader2, CalendarRange } from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth } from "date-fns";
import { formatTime, tzForCountry, tzShort } from "@/lib/timezone";

// Colors matched to StatusPill palette.
const STATUS_STYLES = {
  Present: "bg-emerald-500/20 border-emerald-400/60 text-emerald-200",
  Absent: "bg-red-500/15 border-red-400/50 text-red-200",
  Future: "bg-secondary/40 border-border text-muted-foreground",
};

const TAG_STYLES = {
  Late: "bg-amber-500/20 text-amber-200 border-amber-400/50",
  Incomplete: "bg-orange-500/20 text-orange-200 border-orange-400/50",
  Breach: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/50",
  "Missing punch-out": "bg-red-500/20 text-red-200 border-red-400/50",
};

const TAG_ABBREV = {
  Late: "L",
  Incomplete: "I",
  Breach: "B",
  "Missing punch-out": "M",
};

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function AttendanceMatrix() {
  const [countries, setCountries] = useState([]);
  const [centers, setCenters] = useState([]);
  const today = new Date();
  const [filters, setFilters] = useState({
    country_id: "",
    center_id: "",
    start: format(startOfMonth(today), "yyyy-MM-dd"),
    end: format(endOfMonth(today), "yyyy-MM-dd"),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Timezone: country's tz when a country is selected, else browser local.
  const activeTz = useMemo(() => tzForCountry(countries, filters.country_id), [countries, filters.country_id]);
  const tzLabel = useMemo(() => tzShort(activeTz), [activeTz]);

  // Load reference data once
  useEffect(() => {
    (async () => {
      const [c, ce] = await Promise.all([
        api.get("/countries"),
        api.get("/centers"),
      ]);
      setCountries(c.data);
      setCenters(ce.data);
    })();
  }, []);

  // Reset center when country changes if center is not in that country
  useEffect(() => {
    if (!filters.country_id) return;
    const stillValid = centers.find(
      (c) => c.id === filters.center_id && c.country_id === filters.country_id
    );
    if (filters.center_id && !stillValid) {
      setFilters((f) => ({ ...f, center_id: "" }));
    }
  }, [filters.country_id, centers, filters.center_id]);

  const centersForCountry = useMemo(() => {
    if (!filters.country_id) return centers;
    return centers.filter((c) => c.country_id === filters.country_id);
  }, [filters.country_id, centers]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        start: filters.start,
        end: filters.end,
      };
      if (filters.country_id) params.country_id = filters.country_id;
      if (filters.center_id) params.center_id = filters.center_id;
      const { data: d } = await api.get("/attendance/matrix", { params });
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load matrix");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.start && filters.end) load();
    // eslint-disable-next-line
  }, [filters.start, filters.end, filters.country_id, filters.center_id]);

  const jumpThisMonth = () => {
    const now = new Date();
    setFilters((f) => ({
      ...f,
      start: format(startOfMonth(now), "yyyy-MM-dd"),
      end: format(endOfMonth(now), "yyyy-MM-dd"),
    }));
  };

  const jumpLastMonth = () => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setFilters((f) => ({
      ...f,
      start: format(startOfMonth(prev), "yyyy-MM-dd"),
      end: format(endOfMonth(prev), "yyyy-MM-dd"),
    }));
  };

  // Aggregate totals across all employees for the summary bar.
  const totals = useMemo(() => {
    if (!data?.employees?.length) return null;
    const t = { present: 0, absent: 0, late: 0, incomplete: 0, breaches: 0, missing_out: 0 };
    for (const e of data.employees) {
      t.present += e.summary.present;
      t.absent += e.summary.absent;
      t.late += e.summary.late;
      t.incomplete += e.summary.incomplete;
      t.breaches += e.summary.breaches;
      t.missing_out += e.summary.missing_out;
    }
    return t;
  }, [data]);

  return (
    <>
      <PageHeader
        eyebrow="Records"
        title="Monthly View"
        subtitle={`Attendance grid by location and date range · times shown in ${tzLabel}${filters.country_id ? " (country local)" : " (your local)"}`}
      />
      <div className="p-8 space-y-4">
        {/* ---------- FILTERS ---------- */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="label-mono">Country</Label>
            <Select
              value={filters.country_id || "__all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, country_id: v === "__all" ? "" : v }))}
            >
              <SelectTrigger data-testid="filter-country" className="rounded-none">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">Center / Location</Label>
            <Select
              value={filters.center_id || "__all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, center_id: v === "__all" ? "" : v }))}
            >
              <SelectTrigger data-testid="filter-center" className="rounded-none">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All centers</SelectItem>
                {centersForCountry.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">From</Label>
            <Input
              data-testid="matrix-start"
              type="date"
              value={filters.start}
              max={filters.end}
              onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="label-mono">To</Label>
            <Input
              data-testid="matrix-end"
              type="date"
              value={filters.end}
              min={filters.start}
              onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
              className="rounded-none"
            />
          </div>
          <div className="col-span-2 md:col-span-2 flex gap-2">
            <Button
              variant="outline"
              className="rounded-none border-border flex-1"
              onClick={jumpThisMonth}
              data-testid="jump-this-month"
            >
              <CalendarRange className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> This month
            </Button>
            <Button
              variant="outline"
              className="rounded-none border-border flex-1"
              onClick={jumpLastMonth}
              data-testid="jump-last-month"
            >
              Last month
            </Button>
          </div>
        </div>

        {/* ---------- LEGEND ---------- */}
        <Card className="rounded-none border-border p-3 flex flex-wrap gap-2 items-center">
          <span className="label-mono mr-1">Legend</span>
          <LegendChip className={STATUS_STYLES.Present} label="Present" />
          <LegendChip className={STATUS_STYLES.Absent} label="Absent" />
          <LegendChip className={STATUS_STYLES.Future} label="Future" />
          <div className="mx-2 h-4 w-px bg-border" />
          <LegendChip className={TAG_STYLES.Late} label="L · Late" />
          <LegendChip className={TAG_STYLES.Incomplete} label="I · Incomplete hours" />
          <LegendChip className={TAG_STYLES.Breach} label="B · Presence breach" />
          <LegendChip className={TAG_STYLES["Missing punch-out"]} label="M · Missing punch-out" />
          {data?.min_hours ? (
            <span className="label-mono text-muted-foreground ml-auto">
              Incomplete threshold · {data.min_hours} h
            </span>
          ) : null}
        </Card>

        {/* ---------- SUMMARY TOTALS ---------- */}
        {totals && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <SummaryTile label="Present" value={totals.present} className="text-emerald-300" />
            <SummaryTile label="Absent" value={totals.absent} className="text-red-300" />
            <SummaryTile label="Late" value={totals.late} className="text-amber-300" />
            <SummaryTile label="Incomplete" value={totals.incomplete} className="text-orange-300" />
            <SummaryTile label="Breaches" value={totals.breaches} className="text-fuchsia-300" />
            <SummaryTile label="Missing out" value={totals.missing_out} className="text-red-300" />
          </div>
        )}

        {/* ---------- ERROR ---------- */}
        {error && (
          <div className="border border-red-500/40 bg-red-500/10 text-red-200 p-3 text-sm">
            {error}
          </div>
        )}

        {/* ---------- MATRIX ---------- */}
        <div className="border border-border overflow-auto max-w-full relative">
          {loading && (
            <div className="absolute inset-0 grid place-items-center bg-background/60 z-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {data && data.employees.length === 0 && !loading && (
            <div className="text-center text-muted-foreground py-16">
              No employees match the selected filters.
            </div>
          )}
          {data && data.employees.length > 0 && (
            <table className="border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-10 bg-card px-3 py-2 text-left label-mono border-r border-border min-w-[220px]">
                    Employee
                  </th>
                  {data.days.map((d) => {
                    const day = parseISO(d);
                    const dow = day.getDay(); // 0 = Sun, 6 = Sat
                    const isWeekend = dow === 0 || dow === 6;
                    return (
                      <th
                        key={d}
                        className={`px-1 py-1 text-center font-mono-brand border-r border-border w-9 ${
                          isWeekend ? "bg-secondary/40" : ""
                        }`}
                        title={format(day, "EEE, d MMM yyyy")}
                      >
                        <div className="text-[9px] text-muted-foreground leading-none">
                          {format(day, "EEEEE")}
                        </div>
                        <div className="text-[11px] leading-tight tabular-nums">
                          {format(day, "d")}
                        </div>
                      </th>
                    );
                  })}
                  <th className="sticky right-0 z-10 bg-card px-3 py-2 text-left label-mono border-l border-border min-w-[180px]">
                    Summary
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((e) => (
                  <tr key={e.id} className="border-b border-border hover:bg-secondary/30">
                    <td
                      className="sticky left-0 bg-background px-3 py-2 border-r border-border align-top"
                      data-testid={`matrix-emp-${e.employee_id}`}
                    >
                      <div className="font-medium leading-tight truncate max-w-[220px]" title={e.name}>
                        {e.name}
                      </div>
                      <div className="font-mono-brand text-[10px] text-muted-foreground mt-0.5">
                        {e.employee_id}
                      </div>
                      {e.center_names?.length > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[220px]" title={e.center_names.join(", ")}>
                          {e.center_names.join(" · ")}
                        </div>
                      )}
                    </td>

                    {data.days.map((day) => {
                      const cell = e.days[day];
                      return (
                        <td
                          key={day}
                          className="border-r border-border p-0.5 align-middle"
                        >
                          <DayCell cell={cell} day={day} employee={e} tz={activeTz} />
                        </td>
                      );
                    })}

                    <td className="sticky right-0 bg-background px-3 py-2 border-l border-border align-top text-[10px]">
                      <SummaryInline s={e.summary} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function LegendChip({ className, label }) {
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono-brand ${className}`}>
      {label}
    </span>
  );
}

function SummaryTile({ label, value, className }) {
  return (
    <Card className="rounded-none border-border p-3">
      <div className="label-mono">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums ${className}`}>{value}</div>
    </Card>
  );
}

function SummaryInline({ s }) {
  return (
    <div className="flex flex-wrap gap-1.5 font-mono-brand">
      <span className="text-emerald-300">P {s.present}</span>
      <span className="text-red-300">A {s.absent}</span>
      {s.late > 0 && <span className="text-amber-300">L {s.late}</span>}
      {s.incomplete > 0 && <span className="text-orange-300">I {s.incomplete}</span>}
      {s.breaches > 0 && <span className="text-fuchsia-300">B {s.breaches}</span>}
      {s.missing_out > 0 && <span className="text-red-300">M {s.missing_out}</span>}
    </div>
  );
}

function DayCell({ cell, day, employee, tz }) {
  if (!cell) return null;
  const base = STATUS_STYLES[cell.status] || "";

  const title = [
    `${employee.name} · ${format(parseISO(day), "d MMM yyyy")}`,
    `Status: ${cell.status}`,
    cell.raw_status && cell.raw_status !== "Present" ? `Detail: ${cell.raw_status}` : null,
    cell.hours != null && cell.status === "Present" ? `Hours: ${cell.hours}h` : null,
    cell.breaches ? `Breaches: ${cell.breaches}` : null,
    cell.punch_in ? `In: ${formatTime(cell.punch_in, tz)}` : null,
    cell.punch_out ? `Out: ${formatTime(cell.punch_out, tz)}` : null,
    cell.center_name ? `Center: ${cell.center_name}` : null,
    cell.tags?.length ? `Tags: ${cell.tags.join(", ")}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div
      className={`w-8 h-8 mx-auto border grid grid-cols-2 grid-rows-2 gap-[1px] p-[2px] ${base}`}
      title={title}
      data-testid={`cell-${employee.employee_id}-${day}`}
    >
      {/* Top-left = status dot */}
      <div className="col-start-1 row-start-1 flex items-center justify-center">
        <div
          className={`h-1.5 w-1.5 rounded-full ${
            cell.status === "Present"
              ? "bg-emerald-400"
              : cell.status === "Absent"
                ? "bg-red-400"
                : "bg-muted-foreground/50"
          }`}
        />
      </div>
      {/* Tag corners: L (top-right), I (bottom-left), B (bottom-right) */}
      {cell.tags?.includes("Late") && (
        <TagDot cls={TAG_STYLES.Late} letter="L" area="col-start-2 row-start-1" />
      )}
      {cell.tags?.includes("Incomplete") && (
        <TagDot cls={TAG_STYLES.Incomplete} letter="I" area="col-start-1 row-start-2" />
      )}
      {cell.tags?.includes("Breach") && (
        <TagDot cls={TAG_STYLES.Breach} letter="B" area="col-start-2 row-start-2" />
      )}
      {cell.tags?.includes("Missing punch-out") && !cell.tags?.includes("Breach") && (
        <TagDot cls={TAG_STYLES["Missing punch-out"]} letter="M" area="col-start-2 row-start-2" />
      )}
    </div>
  );
}

function TagDot({ cls, letter, area }) {
  return (
    <div className={`${area} flex items-center justify-center`}>
      <span className={`text-[7px] font-mono-brand leading-none border ${cls} px-[2px]`}>
        {letter}
      </span>
    </div>
  );
}
